/**
 * Presentation helpers for maintenance items — icons, colour ramps,
 * category labels. Kept out of the components so both the summary card
 * and the full page render items identically.
 */
import {
  Droplet,
  Disc3,
  Filter,
  Wrench,
  Zap,
  Wind,
  Battery,
  Circle,
} from "lucide-react";

// Category → { icon component, tone } for consistent rendering.
export const CATEGORY_META = {
  oil: { icon: Droplet, label: "Oil" },
  tires: { icon: Disc3, label: "Tires" },
  brakes: { icon: Disc3, label: "Brakes" },
  fluid: { icon: Droplet, label: "Fluid" },
  filter: { icon: Filter, label: "Filter" },
  spark_plugs: { icon: Zap, label: "Ignition" },
  battery: { icon: Battery, label: "Battery" },
  wipers: { icon: Wind, label: "Wipers" },
  custom: { icon: Wrench, label: "Custom" },
};

export function categoryIcon(cat) {
  return (CATEGORY_META[cat] || CATEGORY_META.custom).icon;
}

export function categoryLabel(cat) {
  return (CATEGORY_META[cat] || CATEGORY_META.custom).label;
}

// Status → colour + label. Colours are hex to match the app's Tesla palette
// (red for danger, amber for warning, green for healthy). Neutral for items
// without an interval configured.
export const STATUS_META = {
  overdue: { label: "Overdue", color: "#FF3B30", ring: "rgba(255,59,48,0.20)" },
  due_soon: { label: "Due Soon", color: "#FFB340", ring: "rgba(255,179,64,0.20)" },
  healthy: { label: "Healthy", color: "#34D17A", ring: "rgba(52,209,122,0.18)" },
  unknown: { label: "No Interval", color: "#8A8A8E", ring: "rgba(138,138,142,0.18)" },
};

export function statusMeta(status) {
  return STATUS_META[status] || STATUS_META.unknown;
}

/**
 * Short "5,432 km left · 128 days" summary used inline in the list. We
 * favour whichever remaining budget is closer to zero so users see the
 * most urgent trigger first.
 */
export function formatRemaining(item, unit = "km") {
  const kmR = item?.km_remaining;
  const dR = item?.days_remaining;
  const hasKm = typeof kmR === "number" && Number.isFinite(kmR);
  const hasD = typeof dR === "number" && Number.isFinite(dR);
  if (!hasKm && !hasD) return "";

  const kmToUnit = (km) => (unit === "mi" ? km / 1.60934 : km);
  const unitLabel = unit === "mi" ? "mi" : "km";

  const parts = [];
  if (hasKm) {
    const disp = kmToUnit(kmR);
    if (disp <= 0) parts.push(`${Math.abs(Math.round(disp)).toLocaleString()} ${unitLabel} over`);
    else parts.push(`${Math.round(disp).toLocaleString()} ${unitLabel} left`);
  }
  if (hasD) {
    if (dR <= 0) parts.push(`${Math.abs(dR)}d over`);
    else parts.push(`${dR}d left`);
  }
  return parts.join(" · ");
}

/**
 * Format "Every 8,000 km / 6 months" style interval blurb.
 */
export function formatInterval(item, unit = "km") {
  const km = item?.interval_km;
  const months = item?.interval_months;
  const parts = [];
  const unitLabel = unit === "mi" ? "mi" : "km";
  const kmToUnit = (v) => (unit === "mi" ? v / 1.60934 : v);
  if (typeof km === "number" && km > 0) {
    parts.push(`${Math.round(kmToUnit(km)).toLocaleString()} ${unitLabel}`);
  }
  if (typeof months === "number" && months > 0) {
    parts.push(months === 12 ? "1 yr" : months < 12 ? `${months} mo` : `${(months / 12).toFixed(1)} yr`);
  }
  return parts.length ? `Every ${parts.join(" / ")}` : "No interval";
}

// Sort helper: overdue first, then due_soon (closest first), then healthy,
// then unknown. Ties break by name.
const ORDER = { overdue: 0, due_soon: 1, healthy: 2, unknown: 3 };
export function sortByUrgency(items) {
  return [...items].sort((a, b) => {
    const so = ORDER[a.status] - ORDER[b.status];
    if (so !== 0) return so;
    // Within a bucket, more overdue / closer due first.
    const aR = urgencyScore(a);
    const bR = urgencyScore(b);
    const cmp = compareUrgency(aR, bR);
    if (cmp !== 0) return cmp;
    return (a.name || "").localeCompare(b.name || "");
  });
}

function urgencyScore(it) {
  // Mirror of the backend _urgency_score — returns an array tuple so ties
  // on the km-equivalent score fall back to raw km_remaining then days_remaining.
  // Array comparison via `sortByUrgency` below uses element-wise ordering.
  const DAILY_KM_ESTIMATE = 40;
  const km = it.km_remaining;
  const d = it.days_remaining;
  const kmScore = typeof km === "number" ? km : Infinity;
  const daysScore = typeof d === "number" ? d * DAILY_KM_ESTIMATE : Infinity;
  const primary = Math.min(kmScore, daysScore);
  if (primary === Infinity) return null;
  const kmTie = typeof km === "number" ? km : Infinity;
  const daysTie = typeof d === "number" ? d : Infinity;
  return [primary, kmTie, daysTie];
}

function compareUrgency(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  for (let i = 0; i < 3; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}
